/**
 * Definição de todas as rotas da SPA (React Router v6, nested). Rotas privadas
 * ficam sob <PrivateRoutes>; constantes de path em routes.enum.ts — adicionar
 * novas rotas SEMPRE pelo enum.
 *
 * Code splitting (set/2026): só ficam no bundle inicial as páginas do caminho
 * crítico — login/cadastro (SignIn/SignUp), o Dashboard (destino pós-login) e
 * os layouts. TODO o resto é `React.lazy` (um chunk por página). Medição antes:
 * main.js 3,4 MB (999 KB gzip), com ECharts + zrender = 30% dele apesar de o
 * Dashboard não ter gráfico ECharts (vinham de consumer-units/analises, que
 * eram importados estaticamente). Números pós-mudança no frontend/CLAUDE.md →
 * "Performance". Regra: página nova entra como lazy, salvo se for o destino
 * imediato do login.
 */
import React, { Suspense, lazy } from 'react';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from 'react-router-dom';

import { Loader } from 'components/loader/loader.component';

// CAMINHO CRÍTICO — eager
import SignIn from 'pages/authentication/signin/signin.page';
import SignUp from 'pages/authentication/signup/signup/signup.page';
import { ClientLayout } from 'pages/client/client.layout';
import Dashboard from 'pages/client/dashboard/dashboard.page';
import { AdminLayout } from 'pages/admin/admin.layout';
import NotFound from 'pages/errors/404/404.page';

import PrivateRoutes from './private.routes';
import { ERoutes } from './routes.enum';

import { ModalInvoice } from 'components/modal-invoice/modal-invoice.component';

// PUBLIC ROUTES — lazy (fluxos raros: recuperação de senha, termos, convite, chat)
const NewPassword = lazy(() => import('pages/new-password/new-password.page'));
const PrivacyPolicies = lazy(
  () => import('pages/privacy-policies/privacy-policies.page')
);
const PasswordRecover = lazy(
  () => import('pages/password-recover/password-recover.page')
);
const UseTerms = lazy(() => import('pages/use-terms/use-terms.page'));
const VerifyEmail = lazy(
  () => import('pages/authentication/signup/verify-email/verify-email.page')
);
const ConfirmEmail = lazy(
  () => import('pages/confirm-email/confirm-email.page')
);
const ConfirmWhatsApp = lazy(
  () => import('pages/confirm-whatsapp/confirm-whatsapp.page')
);
const Invitation = lazy(() => import('pages/invitation/invitation.page'));
const TeamInvitation = lazy(
  () => import('pages/invitation/team-invitation.page')
);
const AccountSetup = lazy(() => import('pages/invitation/account-setup.page'));
const UserWA = lazy(() => import('pages/authentication/user-wa/user-wa.page'));
const UserWAConfirmation = lazy(
  () => import('pages/authentication/user-wa/user-wa-confirmation.page')
);
const SolarOportunity = lazy(
  () => import('pages/client/solar-oportunity/solar-oportunity.page')
);
const FatalError = lazy(() => import('pages/errors/500/500.page'));

// CLIENT ROUTES — lazy (consumer-units e analises carregam ECharts; ajuda é texto longo)
const NewConsumerUnit = lazy(
  () =>
    import(
      'pages/client/consumer-units/new-consumer-unit/new-consumer-unit.page'
    )
);
const EditConsumerUnit = lazy(
  () =>
    import(
      'pages/client/consumer-units/update-consumer-unit/update-consumer-unit.page'
    )
);
const ConsumerUnits = lazy(
  () => import('pages/client/consumer-units/consumer-units.page')
);
const Help = lazy(() => import('pages/client/help/help.page'));
const HowToGetInvoice = lazy(
  () =>
    import(
      'pages/client/help/components/how-to-get-invoice/how-to-get-invoice.component'
    )
);
const InjectedEnergyZero = lazy(
  () =>
    import(
      'pages/client/help/components/injected-energy-zero/injected-energy-zero.component'
    )
);
const RemovePDFPassword = lazy(
  () =>
    import(
      'pages/client/help/components/remove-pdf-password/remove-pdf-password.component'
    )
);
const Profile = lazy(() => import('pages/client/profile/profile.page'));
const Account = lazy(() => import('pages/client/account/account.page'));
const Team = lazy(() => import('pages/client/team/team.page'));
const UpdateUser = lazy(
  () => import('pages/client/update-user/update-user.page')
);
const Integrations = lazy(
  () => import('pages/client/integrations/integrations.page')
);
const Analises = lazy(() => import('pages/client/analises/analises.page'));
const ServicosAdicionais = lazy(
  () => import('pages/client/servicos-adicionais/servicos-adicionais.page')
);
// Saiu de Serviços Adicionais em out/2026
const OportunidadesEconomia = lazy(
  () =>
    import('pages/client/oportunidades-economia/oportunidades-economia.page')
);

// Relatório (gráficos + xlsx) — o chunk mais pesado, sempre lazy
const Report = lazy(() => import('pages/client/report/report.page'));
// Relatório de emissões de escopo 2 (Enterprise, out/2026) — mesma moldura do relatório da fatura
const EmissionsReport = lazy(
  () => import('pages/client/emissions-report/emissions-report.page')
);

// ADMIN ROUTES — chunk separado: clientes comuns nunca baixam o painel admin
// Clientes e Faturamento foram fundidos numa tela só (billing.page → "Clientes")
const AdminClients = lazy(() => import('pages/admin/billing/billing.page'));
const AdminClientInvoiceEditor = lazy(
  () => import('pages/admin/client-invoice-editor/client-invoice-editor.page')
);
const AdminClientReport = lazy(
  () => import('pages/admin/client-report/client-report.page')
);
const AdminDashboard = lazy(
  () => import('pages/admin/dashboard/dashboard.page')
);
const AdminBulkUpload = lazy(
  () => import('pages/admin/bulk-upload/bulk-upload.page')
);
const AdminServices = lazy(() => import('pages/admin/services/services.page'));
// Controle de faturamento (cobranças por ciclo + pedido de NF) — out/2026
const AdminBillingControl = lazy(
  () => import('pages/admin/billing-control/billing-control.page')
);
// Sinais de uso das contas (cadastro/exclusão de UCs, titulares) — out/2026
const AdminAccountUsage = lazy(
  () => import('pages/admin/account-usage/account-usage.page')
);
const AdminMarketplace = lazy(
  () => import('pages/admin/marketplace/marketplace.page')
);

const AppRoutes = () => {
  return (
    <Router>
      <ModalInvoice />
      <Suspense fallback={<Loader />}>
        <Routes>
          <Route index path="/" element={<Navigate to="dashboard" replace />} />
          <Route path="/entrar" element={<SignIn />} />
          <Route path="/cadastrar" element={<SignUp />} />
          <Route path={ERoutes.SignUpConfirmEmail} element={<VerifyEmail />} />
          <Route path={ERoutes.ConfirmEmail} element={<ConfirmEmail />} />
          <Route
            path="/politicas-de-privacidade"
            element={<PrivacyPolicies />}
          />
          <Route path="/termos-de-uso" element={<UseTerms />} />
          <Route path="/recuperar-senha" element={<PasswordRecover />} />
          <Route path="/nova-senha" element={<NewPassword />} />
          <Route path="/erro-fatal" element={<FatalError />} />
          <Route path="/convite" element={<Invitation />} />
          <Route path="/convite-equipe" element={<TeamInvitation />} />
          <Route path={ERoutes.AccountSetup} element={<AccountSetup />} />
          <Route path="/chat" element={<UserWA />} />
          <Route
            path="/whatsapp-verificado/:token"
            element={<ConfirmWhatsApp />}
          />
          <Route
            path="/oportunidade-energia-solar/:consumerUnitId"
            element={<SolarOportunity />}
          />
          <Route path="/chat-confirmacao" element={<UserWAConfirmation />} />
          <Route path="*" element={<NotFound />} />

          <Route element={<PrivateRoutes />}>
            <Route element={<ClientLayout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route
                path="/unidades-consumidoras"
                element={<ConsumerUnits />}
              />
              <Route path="/integracoes" element={<Integrations />} />
              <Route
                path="/unidades-consumidoras/adicionar"
                element={<NewConsumerUnit />}
              />
              <Route path="/perfil" element={<Profile />} />
              <Route path="/conta" element={<Account />} />
              <Route path="/equipe" element={<Team />} />
              <Route
                path="/unidades-consumidoras/:consumerUnitId"
                element={<EditConsumerUnit />}
              />
              {/* Consolidado em "Minhas Integrações" (/integracoes) — mantido como
                redirect para links/bookmarks antigos */}
              <Route
                path="/meus-monitoramentos"
                element={<Navigate to="/integracoes" replace />}
              />
              {/* mesma página do admin — a lógica usa o token do usuário logado */}
              <Route path="/upload-faturas" element={<AdminBulkUpload />} />
              <Route path="/analises" element={<Analises />} />
              <Route
                path="/servicos-adicionais"
                element={<ServicosAdicionais />}
              />
              <Route
                path="/oportunidades-economia"
                element={<OportunidadesEconomia />}
              />
              <Route path="/ajuda" element={<Help />}>
                <Route
                  path="como-obter-fatura-em-pdf"
                  element={<HowToGetInvoice />}
                />
                {/* FAQ antigo (só conceitos) → Problemas comuns (out/2026) */}
                <Route
                  path="perguntas-frequentes"
                  element={
                    <Navigate to="/ajuda?topico=faq-problemas" replace />
                  }
                />
                <Route
                  path="energia-injetada-zero"
                  element={<InjectedEnergyZero />}
                />
                <Route
                  path="remover-senha-arquivo-pdf"
                  element={<RemovePDFPassword />}
                />
                <Route
                  path="adicionar-sistema-monitoramento-unidade-consumidora"
                  element={<Navigate to="/ajuda?topico=pp-inversor" replace />}
                />
              </Route>
            </Route>
            <Route path="/atualizar-dados" element={<UpdateUser />} />
            <Route path="/relatorio/:invoiceId" element={<Report />} />
            <Route path="/relatorio-emissoes" element={<EmissionsReport />} />
          </Route>

          <Route path="/admin" element={<AdminLayout />}>
            <Route path="clientes" element={<AdminClients />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="upload-faturas" element={<AdminBulkUpload />} />
            <Route path="servicos" element={<AdminServices />} />
            <Route path="faturamento" element={<AdminBillingControl />} />
            <Route path="uso-contas" element={<AdminAccountUsage />} />
            <Route path="marketplace" element={<AdminMarketplace />} />
            <Route
              path="clientes/:clientId/relatorio/:invoiceId"
              element={<AdminClientReport />}
            />
            <Route
              path="clientes/:clientId/faturas/:invoiceId/editor"
              element={<AdminClientInvoiceEditor />}
            />
          </Route>
        </Routes>
      </Suspense>
    </Router>
  );
};

export default AppRoutes;
